import { useId, useState } from 'react'

import { sectionCardTitleH6 } from './FormCard'

interface TermsDisclosureCardProps {
  title: string
  summary: string
  children: React.ReactNode
}

/**
 * Standalone card that presents an attestation/disclosure title and summary with
 * a centered tertiary toggle to reveal full legal terms inline.
 *
 * Layout: title → summary → centered "View Full Terms" button, all within the
 * shared `.order-card` chrome. The toggle is never pinned to the title row.
 */
export function TermsDisclosureCard({ title, summary, children }: TermsDisclosureCardProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()

  return (
    <div className="order-card w-full min-w-0 !p-4 sm:!p-7">
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <h2 className={sectionCardTitleH6}>{title}</h2>

        <p className="text-sm leading-5 text-text-body">{summary}</p>

        <div className="flex justify-center">
          <button
            type="button"
            className="rounded-full border border-border-base bg-white px-3 py-1.5 text-xs font-medium text-text-body shadow-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide full terms' : 'View Full Terms'}
          </button>
        </div>

        {open ? (
          <div
            id={panelId}
            className="border-t border-border-base pt-4 text-sm leading-6 text-text-body dark:border-gray-700 dark:text-gray-300"
          >
            {children}
          </div>
        ) : null}
      </div>
    </div>
  )
}
